/**
 * Financial Service
 * Handles all financial and cost management API calls
 */

import { httpClient, ApiResponse } from './http.client';

// =================== INTERFACES ===================

export interface CostCode {
  cost_code_id: string;
  organization_id: string;
  project_id?: string;
  code: string;
  name: string;
  description?: string;
  category: 'labor' | 'materials' | 'equipment' | 'subcontractors' | 'other';
  parent_code_id?: string;
  level: number;
  budgeted_amount?: number;
  actual_cost?: number;
  committed_cost?: number;
  remaining_budget?: number;
  unit_of_measure?: string;
  budgeted_quantity?: number;
  actual_quantity?: number;
  unit_cost?: number;
  is_active: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface TimesheetEntry {
  entry_id: string;
  timesheet_id: string;
  project_id: string;
  cost_code_id?: string;
  task_description: string;
  work_date: string;
  start_time?: string;
  end_time?: string;
  hours_worked: number;
  overtime_hours?: number;
  regular_rate?: number;
  overtime_rate?: number;
  total_cost?: number;
  location?: string;
  equipment_used?: string[];
  created_at: string;
  updated_at: string;
}

export interface Timesheet {
  timesheet_id: string;
  organization_id: string;
  employee_id: string;
  employee_name: string;
  employee_number?: string;
  period_start: string;
  period_end: string;
  week_ending: string;
  total_regular_hours: number;
  total_overtime_hours: number;
  total_hours: number;
  total_cost: number;
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'paid';
  submitted_at?: string;
  approved_by?: string;
  approved_at?: string;
  rejection_reason?: string;
  entries: TimesheetEntry[];
  created_at: string;
  updated_at: string;
}

export interface Budget {
  budget_id: string;
  project_id: string;
  organization_id: string;
  budget_name: string;
  version: string;
  description?: string;
  total_budget: number;
  total_committed: number;
  total_actual: number;
  remaining_budget: number;
  contingency: number;
  is_approved: boolean;
  approved_by?: string;
  approved_at?: string;
  is_active: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface BudgetLineItem {
  line_item_id: string;
  budget_id: string;
  cost_code_id?: string;
  description: string;
  category: 'labor' | 'materials' | 'equipment' | 'subcontractors' | 'other';
  quantity?: number;
  unit_of_measure?: string;
  unit_cost?: number;
  total_budget: number;
  committed_amount: number;
  actual_amount: number;
  remaining_amount: number;
  created_at: string;
  updated_at: string;
}

export type ContractStatus = 'Draft' | 'Under Review' | 'Active' | 'Completed' | 'Terminated' | 'Suspended';
export type ContractType = 'Main Contract' | 'Subcontract' | 'Supply Agreement' | 'Service Agreement' | 'Consulting' | 'Equipment Rental';

export interface Contract {
  contract_id: string;
  project_id: string;
  organization_id: string;
  supplier_id?: string;
  contract_number: string;
  contract_title: string;
  contract_type: ContractType;
  status: ContractStatus;
  client_name?: string;
  contractor_name?: string;
  contract_value: number;
  currency: string;
  payment_terms?: string;
  retention_percentage?: number;
  advance_payment_percentage?: number;
  start_date?: string;
  end_date?: string;
  duration_days?: number;
  scope_of_work?: string;
  terms_and_conditions?: string;
  warranty_period_days?: number;
  contract_document_path?: string;
  signed_contract_path?: string;
  is_signed: boolean;
  signed_date?: string;
  created_by_user_id: string;
  approved_by_user_id?: string;
  approved_at?: string;
  created_at: string;
  updated_at: string;
}

export interface ContractCreateData {
  organization_id: string;
  project_id: string;
  contract_number: string;
  contract_title: string;
  contract_type: ContractType;
  contract_value: number;
  currency?: string;
  status?: ContractStatus;
  client_name?: string;
  contractor_name?: string;
  payment_terms?: string;
  retention_percentage?: number;
  start_date?: string;
  end_date?: string;
  scope_of_work?: string;
  terms_and_conditions?: string;
  warranty_period_days?: number;
  is_signed?: boolean;
  documents?: Array<{ file_id: string; file_path: string }>;
}

export interface ChangeOrder {
  change_order_id: string;
  project_id: string;
  contract_id?: string;
  change_order_number: string;
  title: string;
  description: string;
  reason?: string;
  cost_impact: number;
  time_impact_days?: number;
  status: 'pending' | 'approved' | 'rejected' | 'implemented';
  requested_by: string;
  approved_by?: string;
  approval_date?: string;
  implemented_date?: string;
  created_at: string;
  updated_at: string;
}

export interface Invoice {
  invoice_id: string;
  project_id: string;
  contract_id?: string;
  organization_id: string;
  invoice_number: string;
  invoice_date: string;
  due_date?: string;
  vendor_name: string;
  vendor_address?: string;
  bill_to_address?: string;
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  amount_paid: number;
  amount_due: number;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface InvoiceLineItem {
  line_item_id: string;
  invoice_id: string;
  cost_code_id?: string;
  description: string;
  quantity?: number;
  unit_price?: number;
  total_amount: number;
  created_at: string;
}

export interface Payment {
  payment_id: string;
  invoice_id?: string;
  contract_id?: string;
  project_id: string;
  organization_id: string;
  payment_number: string;
  payment_date: string;
  amount: number;
  payment_method: string;
  reference_number?: string;
  payee_name: string;
  status: 'pending' | 'approved' | 'rejected' | 'paid' | 'cancelled';
  created_by: string;
  approved_by?: string;
  created_at: string;
  updated_at: string;
}

// =================== NEW INTERFACES (matching backend schemas) ===================

export type BudgetType = 'original' | 'revised' | 'forecast' | 'baseline';
export type BudgetStatus = 'draft' | 'active' | 'approved' | 'locked' | 'closed' | 'archived';
export type ChangeOrderType = 'scope_addition' | 'scope_reduction' | 'field_condition' | 'owner_request' | 'design_change' | 'force_majeure' | 'other';
export type CostImpactType = 'cost_increase' | 'cost_decrease' | 'no_cost_impact';
export type ChangeOrderWorkflowStatus = 'draft' | 'pending_approval' | 'approved' | 'rejected' | 'executed';
export type ApprovalLevelRequired = 'project_manager' | 'senior_manager' | 'director' | 'board';

export interface ProjectBudget {
  budget_id: string;
  project_id: string;
  organization_id: string;
  budget_name: string;
  budget_number: string;
  budget_type: BudgetType;
  version: string;
  status: BudgetStatus;
  is_baseline: boolean;
  budget_date: string;
  effective_date: string | null;
  approval_date: string | null;
  approved_by: string | null;
  original_amount: number;
  approved_changes: number;
  pending_changes: number;
  current_budget: number;
  committed_amount: number;
  actual_amount: number;
  forecasted_amount: number;
  remaining_budget: number;
  variance_amount: number;
  variance_percentage: number;
  utilization_percentage: number;
  is_over_budget: boolean;
  labor_budget: number | null;
  material_budget: number | null;
  equipment_budget: number | null;
  subcontractor_budget: number | null;
  other_direct_costs: number | null;
  overhead_amount: number | null;
  overhead_percentage: number | null;
  profit_amount: number | null;
  profit_percentage: number | null;
  contingency_amount: number | null;
  contingency_percentage: number | null;
  risk_reserve_amount: number | null;
  weather_allowance: number | null;
  currency_code: string | null;
  change_order_count: number;
  pending_change_order_count: number;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProjectBudgetSummary {
  project_id: string;
  total_original_budget: number;
  total_current_budget: number;
  total_actual_costs: number;
  total_committed_costs: number;
  total_remaining_budget: number;
  total_variance: number;
  variance_percentage: number;
  active_budgets_count: number;
  pending_change_orders_count: number;
  approved_change_orders_count: number;
  budgets_over_limit: number;
  last_updated: string;
}

export interface ProjectBudgetCreateData {
  organization_id: string;
  project_id: string;
  budget_name: string;
  budget_number: string;
  budget_type: BudgetType;
  version?: string;
  budget_date: string;
  original_amount: number;
  current_budget: number;
  labor_budget?: number;
  material_budget?: number;
  equipment_budget?: number;
  subcontractor_budget?: number;
  other_direct_costs?: number;
  contingency_amount?: number;
  overhead_amount?: number;
  overhead_percentage?: number;
  profit_amount?: number;
  profit_percentage?: number;
  risk_reserve_amount?: number;
  weather_allowance?: number;
  currency_code?: string;
  description?: string;
}

export interface BudgetChangeOrder {
  change_order_id: string;
  budget_id: string;
  project_id: string;
  organization_id: string;
  change_order_number: string;
  title: string;
  description: string | null;
  change_type: ChangeOrderType | null;
  impact_type: CostImpactType | null;
  status: ChangeOrderWorkflowStatus;
  cost_impact: number;
  labor_cost_change: number | null;
  material_cost_change: number | null;
  equipment_cost_change: number | null;
  subcontractor_cost_change: number | null;
  schedule_impact_days: number | null;
  request_date: string | null;
  required_decision_date: string | null;
  reason_for_change: string | null;
  approval_level_required: ApprovalLevelRequired | null;
  current_approver: string | null;
  priority: string | null;
  risk_assessment: string | null;
  risk_level: string | null;
  created_at: string;
  updated_at: string;
}

export interface BudgetChangeOrderCreateData {
  organization_id: string;
  budget_id: string;
  project_id: string;
  change_order_number: string;
  title: string;
  description?: string;
  change_type?: ChangeOrderType;
  impact_type?: CostImpactType;
  cost_impact: number;
  labor_cost_change?: number;
  material_cost_change?: number;
  equipment_cost_change?: number;
  subcontractor_cost_change?: number;
  schedule_impact_days?: number;
  request_date?: string;
  required_decision_date?: string;
  reason_for_change?: string;
  approval_level_required?: ApprovalLevelRequired;
  current_approver?: string;
}

export interface CostCodeItem {
  cost_code_id: string;
  organization_id: string;
  code: string;
  short_description: string;
  long_description?: string;
  level: string;
  type?: string;
  status?: string;
  unit_of_measure?: string;
  budgeted_amount?: number;
  actual_amount?: number;
  committed_amount?: number;
  labor_rate?: number;
  material_rate?: number;
  equipment_rate?: number;
  notes?: string;
  parent_id?: string;
  allow_transactions?: boolean;
  created_at: string;
  updated_at: string;
}

export interface CostCodeCreateData {
  organization_id: string;
  code: string;
  short_description: string;
  long_description?: string;
  level: string;
  type: string;
  unit_of_measure?: string;
  budgeted_amount?: number;
  labor_rate?: number;
  material_rate?: number;
  equipment_rate?: number;
  notes?: string;
  parent_id?: string;
}

export interface TimesheetRecord {
  timesheet_id: string;
  user_id: string;
  project_id: string;
  organization_id?: string;
  timesheet_number?: string;
  work_date: string;
  regular_hours: number;
  overtime_hours: number;
  double_time_hours: number;
  hourly_rate?: number;
  overtime_rate?: number;
  double_time_rate?: number;
  total_regular_pay?: number;
  total_overtime_pay?: number;
  total_pay?: number;
  task_description?: string;
  cost_code_id?: string;
  notes?: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface TimesheetCreateData {
  user_id: string;
  project_id: string;
  work_date: string;
  regular_hours: number;
  overtime_hours?: number;
  double_time_hours?: number;
  hourly_rate?: number;
  overtime_rate?: number;
  double_time_rate?: number;
  task_description?: string;
  cost_code_id?: string;
  notes?: string;
  timesheet_number?: string;
}

export interface APInvoice {
  vendor_invoice_id: string;
  organization_id: string;
  project_id?: string;
  contract_id?: string;
  invoice_number: string;
  vendor_name: string;
  vendor_address?: string;
  po_number?: string;
  invoice_date: string;
  due_date?: string;
  received_date?: string;
  payment_terms?: string;
  currency: string;
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  amount_paid: number;
  description?: string;
  notes?: string;
  department?: string;
  cost_center?: string;
  gl_code?: string;
  approval_required: boolean;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface APInvoiceCreateData {
  organization_id: string;
  invoice_number: string;
  vendor_name: string;
  vendor_address?: string;
  po_number?: string;
  project_id?: string;
  contract_id?: string;
  invoice_date: string;
  due_date?: string;
  received_date?: string;
  payment_terms?: string;
  currency?: string;
  subtotal?: number;
  tax_amount?: number;
  total_amount?: number;
  description?: string;
  notes?: string;
  department?: string;
  cost_center?: string;
  gl_code?: string;
  approval_required?: boolean;
  status?: string;
}

export interface StandalonePayment {
  payment_id: string;
  organization_id: string;
  project_id?: string;
  contract_id?: string;
  payment_number?: string;
  payment_type: string;
  payment_method: string;
  payment_date: string;
  amount: number;
  currency: string;
  reference_number?: string;
  vendor_name?: string;
  client_name?: string;
  description?: string;
  notes?: string;
  bank_account?: string;
  bank_reference?: string;
  discount_amount: number;
  late_fee_amount: number;
  approval_required: boolean;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface StandalonePaymentCreateData {
  organization_id: string;
  project_id?: string;
  contract_id?: string;
  payment_number?: string;
  payment_type?: string;
  payment_method: string;
  payment_date: string;
  amount: number;
  currency?: string;
  reference_number?: string;
  vendor_name?: string;
  client_name?: string;
  description?: string;
  notes?: string;
  bank_account?: string;
  bank_reference?: string;
  discount_amount?: number;
  late_fee_amount?: number;
  approval_required?: boolean;
  status?: string;
}

export interface PayAppLineItem {
  item_id?: string;
  description: string;
  scheduled_value: number;
  work_completed_from_previous: number;
  work_completed_this_period: number;
  materials_stored: number;
  total_completed_and_stored: number;
  percentage_complete: number;
  balance_to_finish: number;
  retention_amount: number;
  sort_order: number;
}

export interface PaymentApp {
  application_id: string;
  organization_id: string;
  project_id?: string;
  contract_id?: string;
  client_name?: string;
  application_number: string;
  application_date: string;
  billing_period_start?: string;
  billing_period_end?: string;
  work_completed_amount: number;
  materials_stored_amount: number;
  change_order_amount: number;
  total_application_amount: number;
  retention_percentage: number;
  retention_amount: number;
  net_payment_due: number;
  currency: string;
  description?: string;
  notes?: string;
  status: string;
  line_items: PayAppLineItem[];
  created_at: string;
  updated_at: string;
}

export interface PaymentAppCreateData {
  organization_id: string;
  project_id?: string;
  contract_id?: string;
  client_name?: string;
  application_number: string;
  application_date: string;
  billing_period_start?: string;
  billing_period_end?: string;
  work_completed_amount?: number;
  materials_stored_amount?: number;
  change_order_amount?: number;
  total_application_amount?: number;
  retention_percentage?: number;
  retention_amount?: number;
  net_payment_due?: number;
  currency?: string;
  description?: string;
  notes?: string;
  status?: string;
  line_items?: Partial<PayAppLineItem>[];
}

// =================== FINANCIAL SERVICE CLASS ===================

class FinancialService {
  // =================== COST CODES ===================
  
  async getCostCodes(projectId?: string, page = 1, perPage = 50): Promise<ApiResponse<{ data: CostCode[]; total: number }>> {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });
    
    if (projectId) {
      queryParams.append('project_id', projectId);
    }

    return httpClient.get<{ data: CostCode[]; total: number }>(
      `/api/v1/cost-codes?${queryParams}`
    );
  }

  async getCostCode(costCodeId: string): Promise<ApiResponse<CostCode>> {
    return httpClient.get<CostCode>(`/api/v1/cost-codes/${costCodeId}`);
  }

  async createCostCode(costCodeData: Partial<CostCode>): Promise<ApiResponse<CostCode>> {
    return httpClient.post<CostCode>('/api/v1/cost-codes', costCodeData);
  }

  async updateCostCode(costCodeId: string, costCodeData: Partial<CostCode>): Promise<ApiResponse<CostCode>> {
    return httpClient.put<CostCode>(`/api/v1/cost-codes/${costCodeId}`, costCodeData);
  }

  async deleteCostCode(costCodeId: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/cost-codes/${costCodeId}`);
  }

  async getCostCodeHierarchy(projectId?: string): Promise<ApiResponse<{ data: CostCode[] }>> {
    const queryParams = projectId ? `?project_id=${projectId}` : '';
    return httpClient.get<{ data: CostCode[] }>(`/api/v1/cost-codes/hierarchy${queryParams}`);
  }

  // =================== TIMESHEETS ===================

  async getTimesheets(orgId: string, employeeId?: string, skip = 0, limit = 100): Promise<ApiResponse<{ data: Timesheet[]; total: number }>> {
    const queryParams = new URLSearchParams({
      organization_id: orgId,
      skip: skip.toString(),
      limit: limit.toString(),
    });

    if (employeeId) {
      queryParams.append('employee_id', employeeId);
    }

    return httpClient.get<{ data: Timesheet[]; total: number }>(
      `/api/v1/timesheets/?${queryParams}`
    );
  }

  async getTimesheet(timesheetId: string): Promise<ApiResponse<Timesheet>> {
    return httpClient.get<Timesheet>(`/api/v1/timesheets/${timesheetId}`);
  }

  async createTimesheet(timesheetData: Partial<Timesheet>): Promise<ApiResponse<Timesheet>> {
    return httpClient.post<Timesheet>('/api/v1/timesheets', timesheetData);
  }

  async updateTimesheet(timesheetId: string, timesheetData: Partial<Timesheet>): Promise<ApiResponse<Timesheet>> {
    return httpClient.put<Timesheet>(`/api/v1/timesheets/${timesheetId}`, timesheetData);
  }

  async deleteTimesheet(timesheetId: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/timesheets/${timesheetId}`);
  }

  async submitTimesheet(timesheetId: string): Promise<ApiResponse<Timesheet>> {
    return httpClient.post<Timesheet>(`/api/v1/timesheets/${timesheetId}/submit`, {});
  }

  async approveTimesheet(timesheetId: string, notes?: string): Promise<ApiResponse<Timesheet>> {
    return httpClient.post<Timesheet>(`/api/v1/timesheets/${timesheetId}/approve`, { notes });
  }

  async rejectTimesheet(timesheetId: string, reason: string): Promise<ApiResponse<Timesheet>> {
    return httpClient.post<Timesheet>(`/api/v1/timesheets/${timesheetId}/reject`, { reason });
  }

  // =================== BUDGETS ===================

  async getBudgets(projectId?: string, page = 1, perPage = 20): Promise<ApiResponse<{ data: Budget[]; total: number }>> {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });
    
    if (projectId) {
      queryParams.append('project_id', projectId);
    }

    return httpClient.get<{ data: Budget[]; total: number }>(
      `/api/v1/budget?${queryParams}`
    );
  }

  async getBudget(budgetId: string): Promise<ApiResponse<Budget & { line_items: BudgetLineItem[] }>> {
    return httpClient.get<Budget & { line_items: BudgetLineItem[] }>(`/api/v1/budget/${budgetId}`);
  }

  async createBudget(budgetData: Partial<Budget>): Promise<ApiResponse<Budget>> {
    return httpClient.post<Budget>('/api/v1/budget', budgetData);
  }

  async updateBudget(budgetId: string, budgetData: Partial<Budget>): Promise<ApiResponse<Budget>> {
    return httpClient.put<Budget>(`/api/v1/budget/${budgetId}`, budgetData);
  }

  async deleteBudget(budgetId: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/budget/${budgetId}`);
  }

  async approveBudget(budgetId: string): Promise<ApiResponse<Budget>> {
    return httpClient.post<Budget>(`/api/v1/budget/${budgetId}/approve`, {});
  }

  async getBudgetVarianceReport(budgetId: string): Promise<ApiResponse<any>> {
    return httpClient.get<any>(`/api/v1/budget/${budgetId}/variance-report`);
  }

  // =================== CONTRACTS ===================

  async getContracts(projectId?: string, skip = 0, limit = 100): Promise<ApiResponse<Contract[]>> {
    const queryParams = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString(),
    });
    if (projectId) queryParams.append('project_id', projectId);
    return httpClient.get<Contract[]>(`/api/v1/contracts?${queryParams}`);
  }

  async getContract(contractId: string): Promise<ApiResponse<Contract>> {
    return httpClient.get<Contract>(`/api/v1/contracts/${contractId}`);
  }

  async createContract(contractData: ContractCreateData): Promise<ApiResponse<Contract>> {
    return httpClient.post<Contract>('/api/v1/contracts', contractData);
  }

  async updateContract(contractId: string, contractData: Partial<ContractCreateData>): Promise<ApiResponse<Contract>> {
    return httpClient.put<Contract>(`/api/v1/contracts/${contractId}`, contractData);
  }

  async deleteContract(contractId: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/contracts/${contractId}`);
  }

  // =================== CHANGE ORDERS ===================

  async getChangeOrders(projectId?: string, contractId?: string, page = 1, perPage = 20): Promise<ApiResponse<{ data: ChangeOrder[]; total: number }>> {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });
    
    if (projectId) queryParams.append('project_id', projectId);
    if (contractId) queryParams.append('contract_id', contractId);

    return httpClient.get<{ data: ChangeOrder[]; total: number }>(
      `/api/v1/change-orders?${queryParams}`
    );
  }

  async getChangeOrder(changeOrderId: string): Promise<ApiResponse<ChangeOrder>> {
    return httpClient.get<ChangeOrder>(`/api/v1/change-orders/${changeOrderId}`);
  }

  async createChangeOrder(changeOrderData: Partial<ChangeOrder>): Promise<ApiResponse<ChangeOrder>> {
    return httpClient.post<ChangeOrder>('/api/v1/change-orders', changeOrderData);
  }

  async updateChangeOrder(changeOrderId: string, changeOrderData: Partial<ChangeOrder>): Promise<ApiResponse<ChangeOrder>> {
    return httpClient.put<ChangeOrder>(`/api/v1/change-orders/${changeOrderId}`, changeOrderData);
  }

  async approveChangeOrder(changeOrderId: string): Promise<ApiResponse<ChangeOrder>> {
    return httpClient.post<ChangeOrder>(`/api/v1/change-orders/${changeOrderId}/approve`, {});
  }

  async rejectChangeOrder(changeOrderId: string, reason: string): Promise<ApiResponse<ChangeOrder>> {
    return httpClient.post<ChangeOrder>(`/api/v1/change-orders/${changeOrderId}/reject`, { reason });
  }

  async implementChangeOrder(changeOrderId: string): Promise<ApiResponse<ChangeOrder>> {
    return httpClient.post<ChangeOrder>(`/api/v1/change-orders/${changeOrderId}/implement`, {});
  }

  // =================== INVOICES ===================

  async getInvoices(projectId?: string, page = 1, perPage = 20, filters?: any): Promise<ApiResponse<{ data: Invoice[]; total: number }>> {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
      ...filters
    });
    
    if (projectId) {
      queryParams.append('project_id', projectId);
    }

    return httpClient.get<{ data: Invoice[]; total: number }>(
      `/api/v1/invoices?${queryParams}`
    );
  }

  async getInvoice(invoiceId: string): Promise<ApiResponse<Invoice & { line_items: InvoiceLineItem[] }>> {
    return httpClient.get<Invoice & { line_items: InvoiceLineItem[] }>(`/api/v1/invoices/${invoiceId}`);
  }

  async createInvoice(invoiceData: Partial<Invoice>): Promise<ApiResponse<Invoice>> {
    return httpClient.post<Invoice>('/api/v1/invoices', invoiceData);
  }

  async updateInvoice(invoiceId: string, invoiceData: Partial<Invoice>): Promise<ApiResponse<Invoice>> {
    return httpClient.put<Invoice>(`/api/v1/invoices/${invoiceId}`, invoiceData);
  }

  async deleteInvoice(invoiceId: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/invoices/${invoiceId}`);
  }

  async sendInvoice(invoiceId: string, recipientEmail?: string): Promise<ApiResponse<Invoice>> {
    return httpClient.post<Invoice>(`/api/v1/invoices/${invoiceId}/send`, { recipient_email: recipientEmail });
  }

  async markInvoiceAsPaid(invoiceId: string, paymentData: { amount: number; payment_date: string; reference?: string }): Promise<ApiResponse<Invoice>> {
    return httpClient.post<Invoice>(`/api/v1/invoices/${invoiceId}/mark-paid`, paymentData);
  }

  // =================== PAYMENTS ===================

  async getPayments(projectId?: string, page = 1, perPage = 20, filters?: any): Promise<ApiResponse<{ data: Payment[]; total: number }>> {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
      ...filters
    });
    
    if (projectId) {
      queryParams.append('project_id', projectId);
    }

    return httpClient.get<{ data: Payment[]; total: number }>(
      `/api/v1/payments?${queryParams}`
    );
  }

  async getPayment(paymentId: string): Promise<ApiResponse<Payment>> {
    return httpClient.get<Payment>(`/api/v1/payments/${paymentId}`);
  }

  async createPayment(paymentData: Partial<Payment>): Promise<ApiResponse<Payment>> {
    return httpClient.post<Payment>('/api/v1/payments', paymentData);
  }

  async updatePayment(paymentId: string, paymentData: Partial<Payment>): Promise<ApiResponse<Payment>> {
    return httpClient.put<Payment>(`/api/v1/payments/${paymentId}`, paymentData);
  }

  async approvePayment(paymentId: string): Promise<ApiResponse<Payment>> {
    return httpClient.post<Payment>(`/api/v1/payments/${paymentId}/approve`, {});
  }

  async processPayment(paymentId: string): Promise<ApiResponse<Payment>> {
    return httpClient.post<Payment>(`/api/v1/payments/${paymentId}/process`, {});
  }

  // =================== REPORTING & ANALYTICS ===================

  async getFinancialSummary(projectId?: string, dateRange?: { start: string; end: string }): Promise<ApiResponse<any>> {
    const queryParams = new URLSearchParams();
    if (projectId) queryParams.append('project_id', projectId);
    if (dateRange) {
      queryParams.append('start_date', dateRange.start);
      queryParams.append('end_date', dateRange.end);
    }
    
    return httpClient.get<any>(`/api/v1/financial/summary?${queryParams}`);
  }

  async getCashFlowReport(projectId?: string, period = 'monthly'): Promise<ApiResponse<any>> {
    const queryParams = new URLSearchParams({ period });
    if (projectId) queryParams.append('project_id', projectId);
    
    return httpClient.get<any>(`/api/v1/financial/cash-flow?${queryParams}`);
  }

  async getProfitabilityReport(projectId?: string): Promise<ApiResponse<any>> {
    const queryParams = projectId ? `?project_id=${projectId}` : '';
    return httpClient.get<any>(`/api/v1/financial/profitability${queryParams}`);
  }

  async getCostBreakdown(projectId: string, groupBy = 'category'): Promise<ApiResponse<any>> {
    const queryParams = new URLSearchParams({ group_by: groupBy });
    return httpClient.get<any>(`/api/v1/financial/cost-breakdown/${projectId}?${queryParams}`);
  }

  async getOutstandingInvoices(): Promise<ApiResponse<{ data: Invoice[] }>> {
    return httpClient.get<{ data: Invoice[] }>('/api/v1/invoices/outstanding');
  }

  async getPaymentApplications(contractId?: string): Promise<ApiResponse<{ data: any[] }>> {
    const queryParams = contractId ? `?contract_id=${contractId}` : '';
    return httpClient.get<{ data: any[] }>(`/api/v1/payment-applications${queryParams}`);
  }

  // =================== PROJECT BUDGETS ===================

  async getProjectBudgets(orgId: string, projectId?: string, skip = 0, limit = 100): Promise<ApiResponse<ProjectBudget[]>> {
    const p = new URLSearchParams({ organization_id: orgId, skip: String(skip), limit: String(limit) });
    if (projectId) p.append('project_id', projectId);
    return httpClient.get<ProjectBudget[]>(`/api/v1/budgets/project-budgets?${p}`);
  }

  async getProjectBudget(budgetId: string): Promise<ApiResponse<ProjectBudget>> {
    return httpClient.get<ProjectBudget>(`/api/v1/budgets/project-budgets/${budgetId}`);
  }

  async createProjectBudget(data: ProjectBudgetCreateData): Promise<ApiResponse<ProjectBudget>> {
    return httpClient.post<ProjectBudget>('/api/v1/budgets/project-budgets', data);
  }

  async updateProjectBudget(budgetId: string, data: Partial<ProjectBudgetCreateData>): Promise<ApiResponse<ProjectBudget>> {
    return httpClient.put<ProjectBudget>(`/api/v1/budgets/project-budgets/${budgetId}`, data);
  }

  async approveProjectBudget(budgetId: string): Promise<ApiResponse<ProjectBudget>> {
    return httpClient.post<ProjectBudget>(`/api/v1/budgets/project-budgets/${budgetId}/approve`, {});
  }

  async setBaselineProjectBudget(budgetId: string): Promise<ApiResponse<ProjectBudget>> {
    return httpClient.post<ProjectBudget>(`/api/v1/budgets/project-budgets/${budgetId}/set-baseline`, {});
  }

  async getProjectBudgetSummary(projectId: string): Promise<ApiResponse<ProjectBudgetSummary>> {
    return httpClient.get<ProjectBudgetSummary>(`/api/v1/budgets/projects/${projectId}/budget-summary`);
  }

  // =================== BUDGET CHANGE ORDERS ===================

  async getBudgetChangeOrders(orgId: string, projectId?: string, budgetId?: string, skip = 0, limit = 100): Promise<ApiResponse<BudgetChangeOrder[]>> {
    const p = new URLSearchParams({ organization_id: orgId, skip: String(skip), limit: String(limit) });
    if (projectId) p.append('project_id', projectId);
    if (budgetId) p.append('budget_id', budgetId);
    return httpClient.get<BudgetChangeOrder[]>(`/api/v1/budgets/change-orders?${p}`);
  }

  async createBudgetChangeOrder(data: BudgetChangeOrderCreateData): Promise<ApiResponse<BudgetChangeOrder>> {
    return httpClient.post<BudgetChangeOrder>('/api/v1/budgets/change-orders', data);
  }

  async updateBudgetChangeOrder(id: string, data: Partial<BudgetChangeOrderCreateData>): Promise<ApiResponse<BudgetChangeOrder>> {
    return httpClient.put<BudgetChangeOrder>(`/api/v1/budgets/change-orders/${id}`, data);
  }

  async submitBudgetChangeOrder(id: string): Promise<ApiResponse<BudgetChangeOrder>> {
    return httpClient.post<BudgetChangeOrder>(`/api/v1/budgets/change-orders/${id}/submit`, {});
  }

  async approveBudgetChangeOrder(id: string): Promise<ApiResponse<BudgetChangeOrder>> {
    return httpClient.post<BudgetChangeOrder>(`/api/v1/budgets/change-orders/${id}/approve`, {});
  }

  async rejectBudgetChangeOrder(id: string, reason?: string): Promise<ApiResponse<BudgetChangeOrder>> {
    return httpClient.post<BudgetChangeOrder>(`/api/v1/budgets/change-orders/${id}/reject`, { rejection_reason: reason });
  }

  async executeBudgetChangeOrder(id: string): Promise<ApiResponse<BudgetChangeOrder>> {
    return httpClient.post<BudgetChangeOrder>(`/api/v1/budgets/change-orders/${id}/execute`, {});
  }

  // =================== COST CODE ITEMS ===================

  async getCostCodeItems(orgId: string, skip = 0, limit = 200): Promise<ApiResponse<CostCodeItem[]>> {
    const p = new URLSearchParams({ organization_id: orgId, skip: String(skip), limit: String(limit) });
    return httpClient.get<CostCodeItem[]>(`/api/v1/cost-codes/cost-codes?${p}`);
  }

  async createCostCodeItem(data: CostCodeCreateData): Promise<ApiResponse<CostCodeItem>> {
    return httpClient.post<CostCodeItem>('/api/v1/cost-codes/cost-codes', data);
  }

  async updateCostCodeItem(id: string, data: Partial<CostCodeCreateData>): Promise<ApiResponse<CostCodeItem>> {
    return httpClient.put<CostCodeItem>(`/api/v1/cost-codes/cost-codes/${id}`, data);
  }

  async deleteCostCodeItem(id: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/cost-codes/cost-codes/${id}`);
  }

  // =================== TIMESHEET RECORDS ===================

  async getTimesheetRecords(orgId: string, projectId?: string, userId?: string, skip = 0, limit = 100): Promise<ApiResponse<TimesheetRecord[]>> {
    const p = new URLSearchParams({ organization_id: orgId, skip: String(skip), limit: String(limit) });
    if (projectId) p.append('project_id', projectId);
    if (userId) p.append('user_id', userId);
    return httpClient.get<TimesheetRecord[]>(`/api/v1/timesheets/?${p}`);
  }

  async createTimesheetRecord(data: TimesheetCreateData): Promise<ApiResponse<TimesheetRecord>> {
    return httpClient.post<TimesheetRecord>('/api/v1/timesheets/', data);
  }

  async submitTimesheetRecord(id: string): Promise<ApiResponse<TimesheetRecord>> {
    return httpClient.post<TimesheetRecord>(`/api/v1/timesheets/${id}/submit`, {});
  }

  async approveTimesheetRecord(id: string): Promise<ApiResponse<TimesheetRecord>> {
    return httpClient.post<TimesheetRecord>(`/api/v1/timesheets/${id}/approve`, {});
  }

  // =================== AP INVOICES ===================

  async getAPInvoices(orgId?: string, projectId?: string, status?: string, skip = 0, limit = 100): Promise<ApiResponse<APInvoice[]>> {
    const p = new URLSearchParams({ skip: String(skip), limit: String(limit) });
    if (orgId) p.append('organization_id', orgId);
    if (projectId) p.append('project_id', projectId);
    if (status) p.append('status', status);
    return httpClient.get<APInvoice[]>(`/api/v1/invoicing/invoices?${p}`);
  }

  async createAPInvoice(data: APInvoiceCreateData): Promise<ApiResponse<APInvoice>> {
    return httpClient.post<APInvoice>('/api/v1/invoicing/invoices', data);
  }

  async updateAPInvoice(id: string, data: Partial<APInvoiceCreateData>): Promise<ApiResponse<APInvoice>> {
    return httpClient.put<APInvoice>(`/api/v1/invoicing/invoices/${id}`, data);
  }

  async deleteAPInvoice(id: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/invoicing/invoices/${id}`);
  }

  async submitAPInvoice(id: string): Promise<ApiResponse<APInvoice>> {
    return httpClient.post<APInvoice>(`/api/v1/invoicing/invoices/${id}/submit`, {});
  }

  async approveAPInvoice(id: string, notes?: string): Promise<ApiResponse<APInvoice>> {
    return httpClient.post<APInvoice>(`/api/v1/invoicing/invoices/${id}/approve`, notes ? { notes } : {});
  }

  async rejectAPInvoice(id: string, rejectionReason: string): Promise<ApiResponse<APInvoice>> {
    return httpClient.post<APInvoice>(`/api/v1/invoicing/invoices/${id}/reject`, { rejection_reason: rejectionReason });
  }

  // =================== STANDALONE PAYMENTS ===================

  async getStandalonePayments(projectId?: string, status?: string, skip = 0, limit = 100): Promise<ApiResponse<StandalonePayment[]>> {
    const p = new URLSearchParams({ skip: String(skip), limit: String(limit) });
    if (projectId) p.append('project_id', projectId);
    if (status) p.append('status', status);
    return httpClient.get<StandalonePayment[]>(`/api/v1/payments/?${p}`);
  }

  async createStandalonePayment(data: StandalonePaymentCreateData): Promise<ApiResponse<StandalonePayment>> {
    return httpClient.post<StandalonePayment>('/api/v1/payments/', data);
  }

  async updateStandalonePayment(id: string, data: Partial<StandalonePaymentCreateData>): Promise<ApiResponse<StandalonePayment>> {
    return httpClient.put<StandalonePayment>(`/api/v1/payments/${id}`, data);
  }

  async deleteStandalonePayment(id: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/payments/${id}`);
  }

  async approveStandalonePayment(id: string): Promise<ApiResponse<StandalonePayment>> {
    return httpClient.post<StandalonePayment>(`/api/v1/payments/${id}/approve`, {});
  }

  // =================== PAYMENT APPLICATIONS ===================

  async getPaymentApps(orgId?: string, projectId?: string, status?: string, skip = 0, limit = 100): Promise<ApiResponse<PaymentApp[]>> {
    const p = new URLSearchParams({ skip: String(skip), limit: String(limit) });
    if (orgId) p.append('organization_id', orgId);
    if (projectId) p.append('project_id', projectId);
    if (status) p.append('status', status);
    return httpClient.get<PaymentApp[]>(`/api/v1/invoicing/payment-applications?${p}`);
  }

  async createPaymentApp(data: PaymentAppCreateData): Promise<ApiResponse<PaymentApp>> {
    return httpClient.post<PaymentApp>('/api/v1/invoicing/payment-applications', data);
  }

  async updatePaymentApp(id: string, data: Partial<PaymentAppCreateData>): Promise<ApiResponse<PaymentApp>> {
    return httpClient.put<PaymentApp>(`/api/v1/invoicing/payment-applications/${id}`, data);
  }

  async deletePaymentApp(id: string): Promise<ApiResponse<void>> {
    return httpClient.delete<void>(`/api/v1/invoicing/payment-applications/${id}`);
  }

  async submitPaymentApp(id: string): Promise<ApiResponse<PaymentApp>> {
    return httpClient.post<PaymentApp>(`/api/v1/invoicing/payment-applications/${id}/submit`, {});
  }

  async approvePaymentApp(id: string, approvedAmount: number, notes?: string): Promise<ApiResponse<PaymentApp>> {
    return httpClient.post<PaymentApp>(`/api/v1/invoicing/payment-applications/${id}/approve`, {
      approved_amount: approvedAmount,
      ...(notes ? { notes } : {}),
    });
  }

  // =================== UTILITY METHODS ===================

  async exportFinancialData(type: 'timesheets' | 'invoices' | 'payments' | 'budget', filters?: any): Promise<ApiResponse<{ download_url: string }>> {
    return httpClient.post<{ download_url: string }>('/api/v1/financial/export', { type, filters });
  }

  async getFinancialDashboard(projectId?: string): Promise<ApiResponse<any>> {
    const queryParams = projectId ? `?project_id=${projectId}` : '';
    return httpClient.get<any>(`/api/v1/financial/dashboard${queryParams}`);
  }

  async validateBudgetAllocation(budgetId: string): Promise<ApiResponse<{ is_valid: boolean; errors?: string[] }>> {
    return httpClient.post<{ is_valid: boolean; errors?: string[] }>(`/api/v1/budget/${budgetId}/validate`, {});
  }

  async generatePayrollReport(payPeriod: string): Promise<ApiResponse<any>> {
    return httpClient.get<any>(`/api/v1/financial/payroll/${payPeriod}`);
  }
}

// Export singleton instance
export const financialService = new FinancialService();